import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import Modal from 'components/Modal';
import styled from 'styled-components';
import dayjs from 'dayjs';
import InputSearchSuffixIcon from 'components/InputSearchSuffixIcon';
import StaffAttendanceItem from './StaffAttendanceItem';
import { useAppDispatch } from 'store/hooks';
import userActions from 'features/user/services/actions';
import useGetListStaffAttendance from 'features/user/services/hooks/useGetListStaffAttendance';
import shopSelectors from 'services/shop/selectors';
import { useRef } from 'react';
import { useReactToPrint } from 'react-to-print';
import { setLoadingPage } from 'services/UI/sagas';
import { AxiosResponse } from 'axios';
import { IStaffPayrollPrintData } from 'features/report/services/types/reportStaff';
import reportActions from 'features/report/services/actions';
import { IClockInOutResData } from 'features/user/services/types/clock-in-out';
import reportApis from 'features/report/services/apis';
import { StaffClockOutPrint } from '../../Buttons/StaffClockOutPrint';
import { toast } from 'react-toastify';
import RequestAdminLocalPW from 'widgets/RequestAdminLocalPW';

export type ManagementStaffModalRef = {
  open: () => void;
  close: () => void;
};

type Props = {};

const ManagementStaffModal = forwardRef<ManagementStaffModalRef, Props>((props, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const dispatch = useAppDispatch();
  const allSetting = shopSelectors.data.allSetting();
  const { listStaffAttendance, mutate } = useGetListStaffAttendance();
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [showModalVerify, setShowModalVerify] = useState(false);
  const [verifyData, setVerifyData] = useState<{
    isClockIn: boolean;
    pinCode: string;
  } | null>(null);

  useImperativeHandle(ref, () => ({
    open: () => setIsOpen(true),
    close: () => setIsOpen(false),
  }));

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchValue);
    }, 600);

    return () => clearTimeout(timer);
  }, [searchValue]);

  const handleClose = () => {
    setIsOpen(false);
  };

  useEffect(() => {
    if (isOpen) {
      mutate();
    }
  }, [isOpen]);

  const printRef = useRef<HTMLDivElement>(null);
  const onPrint = useReactToPrint({
    content: () => printRef.current,
    copyStyles: true,
  });

  const handleClockIn = async (newPin: string) => {
    await dispatch(userActions.doClockIn.fetch(newPin));

    setTimeout(() => {
      mutate();
      toast.success('Clock in successfully');
    }, 500);
  };

  const handleClockOut = async (newPin: string) => {
    await dispatch(
      userActions.doClockOut.fetch({
        pin: newPin,
        onSuccess: (staffData) => {
          toast.success('Clock out successfully');
          mutate();
          if (allSetting?.enablePrintDailyClockOut) {
            handlePrint(staffData);
          }
        },
      }),
    );
  };

  const handlePrint = async (staffData: IClockInOutResData) => {
    setLoadingPage(true);
    try {
      const res: AxiosResponse<{ data: IStaffPayrollPrintData[] }> = await reportApis.getReportSalaryStaff({
        staffId: staffData.id,
        startDate: dayjs().startOf('day').format('MM-DD-YYYY'),
        endDate: dayjs().endOf('day').format('MM-DD-YYYY'),
      });
      if (res.data.data && res.data.data.length > 0) {
        dispatch(reportActions.getReportSalaryStaff.success(res.data.data));
        setTimeout(() => onPrint(), 100);
      }
    } catch (error) {
      dispatch(reportActions.getReportSalaryStaff.fail({}));
    } finally {
      setLoadingPage(false);
    }
  };

  const callback = (isClockIn: boolean, pinCode: string) => {
    if (isClockIn) {
      handleClockOut(pinCode);
    } else {
      handleClockIn(pinCode);
    }
  };

  const handlePress = async (isClockIn: boolean, pinCode: string) => {
    setShowModalVerify(true);
    setVerifyData({
      isClockIn,
      pinCode,
    });
  };

  const onVerifyAdminSuccess = () => {
    if (verifyData && verifyData.pinCode) {
      callback(verifyData.isClockIn, verifyData.pinCode);
    }
    setShowModalVerify(false);
    setVerifyData(null);
  };

  const filteredList = useMemo(() => {
    if (!debouncedSearch) return listStaffAttendance;

    return listStaffAttendance?.filter((item) => item.staffName?.toLowerCase().includes(debouncedSearch.toLowerCase()));
  }, [debouncedSearch, listStaffAttendance]);

  return (
    <Modal
      hiddenOkButton={true}
      visible={isOpen}
      onClose={handleClose}
      width={'70vw'}
      v2
      modalTitle='STAFF MANAGEMENT'
      noneBodyStyle={true}
      zIndex={1000}
    >
      <Container>
        <InputSearchSuffixIcon value={searchValue} onChange={setSearchValue} />
        <Content>
          {filteredList?.map((item) => (
            <StaffAttendanceItem key={item.id} item={item} onPress={handlePress} />
          ))}
        </Content>
      </Container>
      {
        <div style={{ display: 'none' }}>
          <div ref={printRef}>
            <StaffClockOutPrint
              params={{
                startDate: dayjs().startOf('day').format('MM-DD-YYYY'),
                endDate: dayjs().endOf('day').format('MM-DD-YYYY'),
              }}
            />
          </div>
        </div>
      }
      {!!showModalVerify && (
        <RequestAdminLocalPW
          onlyAdmin
          visible
          v2
          onCorrect={onVerifyAdminSuccess}
          onClose={() => setShowModalVerify(false)}
        />
      )}
    </Modal>
  );
});

ManagementStaffModal.displayName = 'ManagementStaffModal';
export default ManagementStaffModal;

const Container = styled.div`
  background: #fff;
  display: flex;
  flex-direction: column;
  gap: 32px;
  padding: 24px;
  box-sizing: border-box;
  transition: all 0.3s ease;
`;

const Content = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
`;
